import React, { useState } from 'react';
import { LIST_DEFAULTS, defaultIconColor, type ListAlign, type ListConfig, type ListType } from '../checkout-components/CheckoutList';

interface ListInspectorProps {
  config: ListConfig;
  onConfigChange: (patch: Partial<ListConfig>) => void;
}

const inputCls =
  'w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder:italic placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500';
const labelCls = 'block text-sm text-gray-700 dark:text-gray-300 mb-1';
const divider = <hr className="border-0 border-t border-dashed border-gray-300 dark:border-gray-700" />;

const LIST_TYPES: { value: ListType; label: string }[] = [
  { value: 'check', label: 'Check' },
  { value: 'numero', label: 'Numerada' },
  { value: 'ponto', label: 'Marcador' },
  { value: 'seta', label: 'Seta' },
];
const ALIGNS: { value: ListAlign; label: string }[] = [
  { value: 'left', label: 'Esquerda' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Direita' },
];
const SIZES = [12, 14, 16, 18, 20, 22, 24, 28];

/** Bolinha de cor (seletor nativo), como no painel da Hotmart. */
const ColorDot: React.FC<{ value: string; onChange: (v: string) => void; empty?: boolean; title: string }> = ({ value, onChange, empty, title }) => (
  <label
    className="relative inline-block w-7 h-7 rounded-full border border-gray-300 shadow-inner cursor-pointer overflow-hidden"
    style={{ backgroundColor: empty ? '#f3f4f6' : value }}
    title={title}
  >
    <input
      type="color"
      className="absolute inset-0 opacity-0 cursor-pointer"
      value={empty ? '#ffffff' : value}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const ListInspector: React.FC<ListInspectorProps> = ({ config, onConfigChange }) => {
  const [newItem, setNewItem] = useState('');
  const items = config.items || [];
  const bg = config.backgroundColor || '';
  const itemColor = config.itemColor || LIST_DEFAULTS.itemColor;
  const iconColor = config.iconColor || defaultIconColor(config.listType || LIST_DEFAULTS.listType, itemColor);

  const addItem = () => {
    const text = newItem.trim();
    if (!text) return;
    onConfigChange({ items: [...items, text] });
    setNewItem('');
  };
  const updateItem = (index: number, text: string) => {
    onConfigChange({ items: items.map((it, i) => (i === index ? text : it)) });
  };
  const removeItem = (index: number) => {
    onConfigChange({ items: items.filter((_, i) => i !== index) });
  };
  const moveItem = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onConfigChange({ items: next });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={labelCls}>Tipo de lista</label>
        <select
          className={inputCls}
          value={config.listType || LIST_DEFAULTS.listType}
          onChange={(e) => onConfigChange({ listType: e.target.value as ListType })}
        >
          {LIST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div>
        <label className={labelCls}>Alinhamento</label>
        <select
          className={inputCls}
          value={config.align || LIST_DEFAULTS.align}
          onChange={(e) => onConfigChange({ align: e.target.value as ListAlign })}
        >
          {ALIGNS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">Cor de fundo</span>
        <div className="flex items-center gap-2">
          {bg && (
            <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => onConfigChange({ backgroundColor: '' })}>
              Sem fundo
            </button>
          )}
          <ColorDot title="Cor de fundo" value={bg} empty={!bg} onChange={(v) => onConfigChange({ backgroundColor: v })} />
        </div>
      </div>

      {divider}

      <div>
        <label className={labelCls}>Título da lista</label>
        <input
          type="text"
          className={inputCls}
          placeholder="Digite para inserir um título"
          value={config.title || ''}
          maxLength={120}
          onChange={(e) => onConfigChange({ title: e.target.value })}
        />
      </div>

      {divider}

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">Cor dos Ítens</span>
        <ColorDot title="Cor dos itens" value={itemColor} onChange={(v) => onConfigChange({ itemColor: v })} />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">Cor dos Ícones</span>
        <div className="flex items-center gap-2">
          {config.iconColor && (
            <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => onConfigChange({ iconColor: '' })}>
              Padrão
            </button>
          )}
          <ColorDot title="Cor dos ícones" value={iconColor} onChange={(v) => onConfigChange({ iconColor: v })} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-gray-700 dark:text-gray-300">Tamanho dos Ítens</span>
        <select
          className={`${inputCls} w-24`}
          value={String(config.itemSize || LIST_DEFAULTS.itemSize)}
          onChange={(e) => onConfigChange({ itemSize: parseInt(e.target.value, 10) })}
        >
          {SIZES.map((s) => <option key={s} value={s}>{s}px</option>)}
        </select>
      </div>

      {divider}

      <div>
        <label className={labelCls}>Novo ítem</label>
        <div className="flex gap-2">
          <input
            type="text"
            className={inputCls}
            placeholder="Adicione aqui um novo item..."
            value={newItem}
            maxLength={200}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
          />
          <button
            type="button"
            className="shrink-0 w-10 rounded-md border border-gray-300 dark:border-gray-700 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 disabled:opacity-40"
            onClick={addItem}
            disabled={!newItem.trim()}
            title="Adicionar item"
          >
            +
          </button>
        </div>
      </div>

      <div>
        <label className={labelCls}>Ítens</label>
        {items.length === 0 ? (
          <p className="text-base text-gray-400 leading-snug">Utilize o campo acima para adicionar itens na lista.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li key={index} className="flex items-center gap-1">
                <input
                  type="text"
                  className={`${inputCls} py-1.5`}
                  value={item}
                  maxLength={200}
                  onChange={(e) => updateItem(index, e.target.value)}
                />
                <button type="button" className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="Subir" disabled={index === 0} onClick={() => moveItem(index, -1)}>
                  <i className="ri-arrow-up-s-line"></i>
                </button>
                <button type="button" className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30" title="Descer" disabled={index === items.length - 1} onClick={() => moveItem(index, 1)}>
                  <i className="ri-arrow-down-s-line"></i>
                </button>
                <button type="button" className="p-1 text-red-500 hover:text-red-700" title="Remover" onClick={() => removeItem(index)}>
                  <i className="ri-delete-bin-line"></i>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ListInspector;
